import React, { useEffect } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import { Button } from '../../styles/common/Button.styled';
import { GradientButton } from '../../styles/common/Button.styled';
import * as S from '../../styles/layout/Area.styled';

interface ConfirmModalProps {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  isSingleButton?: boolean;
  appearance?: 'legacy' | 'console';
  tone?: 'primary' | 'danger';
  pending?: boolean;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled(S.Area)`
  width: 400px;
  height: auto;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.text_color_100};
  margin-bottom: 16px;
`;

const Message = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.color.text_color_200};
  line-height: 1.5;
  margin-bottom: 32px;
  white-space: pre-wrap;
`;

const ButtonGroup = styled.div<{ $isSingle?: boolean }>`
  display: flex;
  gap: 12px;
  width: 100%;

  & > * {
    flex: 1;
  }
`;

const SecondaryButton = styled.button`
  height: 48px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.primary_300};
  background-color: transparent;
  color: ${({ theme }) => theme.color.text_color_200};
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
    border-color: ${({ theme }) => theme.color.text_color_400};
  }
`;

const LegacyConfirmModal: React.FC<ConfirmModalProps> = ({
  title = '알림',
  message,
  confirmLabel = '확인',
  cancelLabel = '취소',
  onConfirm,
  onCancel,
  isSingleButton = false,
}) => {
  useEffect(() => {
    window.history.pushState({ isModalOpen: true }, '');
    const handlePopState = () => {
      if (onCancel) onCancel();
      else onConfirm();
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.isModalOpen) {
        window.history.back();
      }
    };
  }, [onCancel, onConfirm]);

  return (
    <ModalOverlay onClick={onCancel || onConfirm}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Title>{title}</Title>
        <Message>{message}</Message>

        <ButtonGroup $isSingle={isSingleButton}>
          {!isSingleButton && (
            <SecondaryButton onClick={onCancel}>
              {cancelLabel}
            </SecondaryButton>
          )}
          <GradientButton
            color1="#A55AAA"
            color2="#8f4294"
            color3="#79397E"
            height={48}
            onClick={onConfirm}
          >
            {confirmLabel}
          </GradientButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

const ConfirmModal: React.FC<ConfirmModalProps> = (props) => {
  if (props.appearance === 'legacy') return <LegacyConfirmModal {...props} />;
  const { title = '알림', message, confirmLabel = '확인', cancelLabel = '취소', onConfirm, onCancel, isSingleButton = false, tone, pending = false } = props;
  return <Modal closeDisabled={pending} title={title} onClose={isSingleButton ? onConfirm : onCancel || (() => undefined)} width={420} footer={<>
    {!isSingleButton && <Button $variant="secondary" disabled={pending} onClick={onCancel}>{cancelLabel}</Button>}
    <Button $variant={tone || (confirmLabel.includes('삭제') ? 'danger' : 'primary')} disabled={pending} onClick={onConfirm}>{pending ? '처리 중...' : confirmLabel}</Button>
  </>}><p style={{ fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{message}</p></Modal>;
};
export default ConfirmModal;
